import { config } from '../../config';
import { BaseCarousel, BaseCarouselConfig, CarouselInstance } from './base-carousel';

const CARD_LIST_SWIPER_HOST = 'data-card-list-swiper-host';
const CARD_LIST_SWIPER_LIST = 'data-card-list-swiper-list';
const CARD_LIST_SELECTOR = '.card-list';
const DEFERRED_HOSTS = 'holiday-search-modal';

class CardCarousel extends BaseCarousel {
  private mediaQuery: MediaQueryList = null;

  protected getDefaultConfig(): BaseCarouselConfig {
    return {
      slidesPerView: 1.15,
      spaceBetween: 10,
      loop: false,
      centeredSlides: true,
      pagination: true,
      navigation: false,
    };
  }

  public init(): void {
    document.querySelectorAll(CARD_LIST_SELECTOR).forEach(container => {
      const element = container as HTMLElement;

      if (element.closest(DEFERRED_HOSTS)) {
        return;
      }

      this.carousels.set(element, {
        container: element,
        swiperInstance: null,
        config: this.getDefaultConfig()
      });
    });

    if (this.carousels.size === 0) {
      return;
    }

    this.handleMediaQueryChange(this.watchViewport());
  }

  /** Registers card lists rendered after page load, initialising them if the viewport calls for it. */
  public register(root: HTMLElement): void {
    const added: CarouselInstance[] = [];

    root.querySelectorAll(CARD_LIST_SELECTOR).forEach(container => {
      const element = container as HTMLElement;

      if (this.carousels.has(element)) {
        return;
      }

      const carouselInstance: CarouselInstance = {
        container: element,
        swiperInstance: null,
        config: this.getDefaultConfig()
      };

      this.carousels.set(element, carouselInstance);
      added.push(carouselInstance);
    });

    if (added.length === 0) {
      return;
    }

    if (this.watchViewport().matches) {
      added.forEach(carouselInstance => this.initCarouselWithCleanup(carouselInstance));
    }
  }

  /** Tears down card lists inside a root that is leaving the DOM, so no stale instance survives it. */
  public unregister(root: HTMLElement): void {
    this.carousels.forEach((carouselInstance, element) => {
      if (!root.contains(element)) {
        return;
      }

      this.destroyCarouselWithCleanup(carouselInstance);
      this.carousels.delete(element);
    });
  }

  private watchViewport(): MediaQueryList {
    if (!this.mediaQuery) {
      this.mediaQuery = window.matchMedia(`(max-width: ${config.breakpoint.xsMax}px)`);
      this.mediaQuery.addEventListener('change', this.handleMediaQueryChange.bind(this));
    }

    return this.mediaQuery;
  }

  private handleMediaQueryChange(mediaQuery: MediaQueryList): void {
    if (mediaQuery.matches) {
      this.initAllCarousels();
    } else {
      this.destroyAllCarousels();
    }
  }

  private initAllCarousels(): void {
    this.carousels.forEach((carouselInstance) => {
      this.initCarouselWithCleanup(carouselInstance);
    });
  }

  private destroyAllCarousels(): void {
    this.carousels.forEach((carouselInstance) => {
      this.destroyCarouselWithCleanup(carouselInstance);
    });
  }

  private initCarouselWithCleanup(carouselInstance: CarouselInstance): void {
    this.destroyCarouselWithCleanup(carouselInstance);
    this.addSwiperStructure(carouselInstance.container, carouselInstance.config);
    this.createSwiperInstance(carouselInstance, CARD_LIST_SELECTOR);
  }

  private destroyCarouselWithCleanup(carouselInstance: CarouselInstance): void {
    this.destroyCarousel(carouselInstance);
    this.removeSwiperStructure(carouselInstance.container);
  }

  private getSwiperHost(container: HTMLElement): HTMLElement | null {
    if (container.hasAttribute(CARD_LIST_SWIPER_LIST)) {
      return container.parentElement;
    }

    if (container.classList.contains('swiper')) {
      return container;
    }

    return null;
  }

  protected addSwiperStructure(container: HTMLElement, config: BaseCarouselConfig): void {
    if (container.classList.contains('swiper') || container.closest('.swiper')) {
      return;
    }

    const isList = container.tagName === 'OL' || container.tagName === 'UL';

    if (isList) {
      const swiperHost = document.createElement('div');
      swiperHost.className = 'swiper swiper-container';
      swiperHost.setAttribute(CARD_LIST_SWIPER_HOST, 'true');

      container.parentNode?.insertBefore(swiperHost, container);
      swiperHost.appendChild(container);

      container.classList.add('swiper-wrapper');
      container.setAttribute(CARD_LIST_SWIPER_LIST, 'true');
      this.addCardListClasses(container);
      this.addSwiperControls(swiperHost, config);
      return;
    }

    container.classList.add('swiper', 'swiper-container');
    this.createSwiperWrapper(container);
    this.addCardListClasses(container);
    this.addSwiperControls(container, config);
  }

  private addCardListClasses(container: HTMLElement): void {
    const cardItems = container.querySelectorAll('.card-list__item');
    cardItems.forEach(item => item.classList.add('swiper-slide'));
  }

  protected removeSwiperStructure(container: HTMLElement): void {
    if (container.hasAttribute(CARD_LIST_SWIPER_LIST)) {
      const swiperHost = container.parentElement;

      if (swiperHost?.hasAttribute(CARD_LIST_SWIPER_HOST)) {
        container.classList.remove('swiper-wrapper');
        container.removeAttribute(CARD_LIST_SWIPER_LIST);

        const cardItems = container.querySelectorAll('.card-list__item');
        cardItems.forEach(item => item.classList.remove('swiper-slide'));

        this.removeSwiperControls(swiperHost);
        swiperHost.parentNode?.insertBefore(container, swiperHost);
        swiperHost.remove();
      }

      return;
    }

    if (!container.classList.contains('swiper')) {
      return;
    }

    container.classList.remove('swiper', 'swiper-container');
    this.restoreOriginalStructure(container);

    const cardItems = container.querySelectorAll('.card-list__item');
    cardItems.forEach(item => item.classList.remove('swiper-slide'));

    this.removeSwiperControls(container);
  }

  protected createSwiperInstance(carouselInstance: CarouselInstance, selector: string): void {
    const listElement = carouselInstance.container;
    const swiperElement = this.getSwiperHost(listElement) ?? listElement;
    const originalContainer = carouselInstance.container;

    carouselInstance.container = swiperElement;
    super.createSwiperInstance(carouselInstance, selector);
    carouselInstance.container = originalContainer;
  }

  protected destroyCarousel(carouselInstance: CarouselInstance): void {
    const listElement = carouselInstance.container;
    const swiperElement = this.getSwiperHost(listElement);
    const originalContainer = carouselInstance.container;

    if (swiperElement) {
      carouselInstance.container = swiperElement;
    }

    super.destroyCarousel(carouselInstance);
    carouselInstance.container = originalContainer;
  }
}

const cardCarousel = new CardCarousel();

export { cardCarousel };
